import localstorage from 'store';
import { optionalCookiesAllowed } from '../../../helpers/optionalCookies';
import { unlockPlayer } from './AovivoBarActions';
import { fetchNowPlayingIfNeeded } from './NowPlayingActions';
// eslint-disable-next-line import/no-cycle
import { setProfileModalState } from '../../profile/actions/ProfileActions';
import Api from '../../../helpers/Api';
import AudioEngine from '../../../helpers/AudioEngine';
import { showError, showInfo } from '../../../common/A1Alert/A1Alert';

export const PLAY_MEDIA_PLAYER = 'PLAY_MEDIA_PLAYER';
export const PLAY_LIVE = 'PLAY_LIVE';
export const PLAY_MEDIA_FROM_SOURCE_WITH_META = 'PLAY_MEDIA_FROM_SOURCE_WITH_META';
export const PAUSE_MEDIA_PLAYER = 'PAUSE_MEDIA_PLAYER';
export const SET_CURRENT_STREAM = 'SET_CURRENT_STREAM';
export const SET_IS_LIVE = 'SET_IS_LIVE';

export const MEDIA_ENDED = 'MEDIA_ENDED';
export const MEDIA_ERROR = 'MEDIA_ERROR';
export const MEDIA_LOADING = 'MEDIA_LOADING';
// export const MEDIA_LOADED = 'MEDIA_LOADED';
export const MEDIA_PLAYING = 'MEDIA_PLAYING';

export const SET_AVAILABLE_STREAMS = 'SET_AVAILABLE_STREAMS';
export const REQUEST_VIDEO_PREROLL = 'REQUEST_VIDEO_PREROLL';
export const COMPLETE_VIDEO_PREROLL = 'COMPLETE_VIDEO_PREROLL';

const TEST_VIDEO_PREROLL_AD_TAG_URL = 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_preroll_skippable&sz=640x480&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&correlator=';

const getEnabledTestValue = (enabledTests, key) => {
    if (!enabledTests) {
        return null;
    }

    if (typeof enabledTests.get === 'function') {
        return enabledTests.get(key);
    }

    return enabledTests[key];
};

const getBrowserTestVideoPrerollAdTagUrl = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    const searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.has('test_videoPrerollAdTagUrl')) {
        return null;
    }

    const testVideoPrerollAdTagUrl = searchParams.get('test_videoPrerollAdTagUrl');

    if (testVideoPrerollAdTagUrl === 'true') {
        return TEST_VIDEO_PREROLL_AD_TAG_URL;
    }

    if (testVideoPrerollAdTagUrl === 'false') {
        return null;
    }

    return testVideoPrerollAdTagUrl;
};

const getVideoPrerollAdTagUrl = (state) => {
    const browserTestVideoPrerollAdTagUrl = getBrowserTestVideoPrerollAdTagUrl();

    if (browserTestVideoPrerollAdTagUrl !== null) {
        return browserTestVideoPrerollAdTagUrl;
    }

    const enabledTests = state.app.enabledTests;
    const testVideoPrerollAdTagUrl = getEnabledTestValue(enabledTests, 'test_videoPrerollAdTagUrl');

    if (testVideoPrerollAdTagUrl === true) {
        return TEST_VIDEO_PREROLL_AD_TAG_URL;
    }

    if (testVideoPrerollAdTagUrl === false) {
        return null;
    }

    if (typeof testVideoPrerollAdTagUrl === 'string' && testVideoPrerollAdTagUrl.length > 0) {
        return testVideoPrerollAdTagUrl;
    }

    return state.app.videoPrerollAdTagUrl;
};

const shouldRequestVideoPreroll = (state, options = {}) => {
    if (options.skipVideoPreroll || !optionalCookiesAllowed()) {
        return false;
    }

    const adTagUrl = getVideoPrerollAdTagUrl(state);

    if (typeof adTagUrl !== 'string' || adTagUrl.trim().length === 0 || adTagUrl === 'false') {
        return false;
    }

    if (state.profile.user.isPro === true) {
        return false;
    }

    if (state.aovivoBar.MediaPlayer.videoPrerollOpen ||
        state.aovivoBar.MediaPlayer.videoPrerollPlayed) {
        return false;
    }

    return adTagUrl.trim();
};


/**
 * Start or continue playback of the player (for Live streams use the playLive function)
 *
 * Note: The source must be loaded before calling this function!
 */
export const playMediaPlayer = () => ({
    type: PLAY_MEDIA_PLAYER,
});

/**
 * set the isLive flag. This is only necessary for the Alternativo stream!
 *
 * @param isLive bool
 */
const setIsLive = (isLive) => ({
    type: SET_IS_LIVE,
    payload: {
        isLive,
    },
});

/**
 * play live stream. use the source from the currentStream
 *
 */
export const playLive = (options = {}) => (dispatch, getState) => {
    const state = getState();
    const currentStream = state.aovivoBar.MediaPlayer.currentStream;

    if (currentStream.isPopup) {
        // open the alternative player in a pop up
        showInfo('O streaming está tocando em uma outra janela!');

        window.open(currentStream.popupUrl, currentStream.popupId, currentStream.popupConfig);

        // reset the isLive flag in case otherMedia was playing
        dispatch(setIsLive(true));

        // stop the player (in case it is playing)
        return dispatch({
            type: PAUSE_MEDIA_PLAYER,
        });
    }

    const videoPrerollAdTagUrl = shouldRequestVideoPreroll(state, options);

    if (videoPrerollAdTagUrl) {
        return dispatch({
            type: REQUEST_VIDEO_PREROLL,
            payload: {
                adTagUrl: videoPrerollAdTagUrl,
            },
        });
    }

    const locked = state.aovivoBar.AovivoBar.locked;

    if (locked) {
        dispatch(unlockPlayer());
    }

    return dispatch({
        type: PLAY_LIVE,
    });
};

export const completeVideoPreroll = () => (dispatch) => {
    dispatch({
        type: COMPLETE_VIDEO_PREROLL,
    });

    AudioEngine.skipNextPrerollAd();

    return dispatch(playLive({ skipVideoPreroll: true }));
};

/**
 * Change the current stream id of the live stream
 *
 * @param streamId String
 */
const setCurrentStream = (streamId) => ({
    type: SET_CURRENT_STREAM,
    payload: {
        streamId,
    },
});

/**
 * Start the player with given source (not LIVE) and set the corresponding meta data
 * Note: To play live radio use playLive().
 *
 * @param sources can be an object or an array of objects of the shape { url: String, type: String }
 * @param meta
 */
export const playMediaFromSourceWithMeta = (sources, meta) => (dispatch, getState) => {
    const locked = getState().aovivoBar.AovivoBar.locked;

    if (locked) {
        dispatch(unlockPlayer());
    }

    dispatch({
        type: PLAY_MEDIA_FROM_SOURCE_WITH_META,
        payload: {
            sources: Array.isArray(sources) ? sources : [sources],
            meta,
        },
    });

    return dispatch(playMediaPlayer());
};

/**
 * Pause the playback of the player (independent if live stream or media)
 *
 */
export const pauseMediaPlayer = () => ({
    type: PAUSE_MEDIA_PLAYER,
});


/**
 * In this current MediaPlayer version where we use playerStatus
 * to control everything, MEDIA_LOADED is not used.
 * It break some functionalities implemented with the new version.
 *
 * This is broadcast when some audio frames have been loaded. This often would mean audio is ready to be
 * played.
 *
 * @returns {{type: string}}
 *
export function onMediaLoaded() {
    return {
        type: MEDIA_LOADED
    };
} */

/**
 * This is broadcast when media playback reaches end. Reducers may be interested in updating some state.
 *
 * @returns {{type: string}}
 */
export function onMediaEnded() {
    return {
        type: MEDIA_ENDED,
    };
}

export function onMediaError() {
    return {
        type: MEDIA_ERROR,
    };
}

/**
 *
 * @returns {{type: string}}
 */
export function onMediaLoading() {
    return {
        type: MEDIA_LOADING,
    };
}

/**
 * This is broadcast when the audio starts playing
 *
 * @returns {{type: string}}
 */
export function onMediaPlaying() {
    return {
        type: MEDIA_PLAYING,
    };
}


/**
 * Set a stream. Check if user is logged in if the stream is a (semi) pro stream
 *
 * @returns {{type: boolean}} (if successfull or not)
 *
 */
export const setStream = (streamId, userInitiated, history) => (dispatch, getState) => {
    // check if this stream existst
    const availableStreams = getState().aovivoBar.MediaPlayer.availableStreams;
    const stream = availableStreams.find((_stream) => _stream.streamId === streamId);

    if (!stream) {
        // Note: this might be the case when a stream was removed by us
        // reset local storage
        localstorage.remove('streamId');

        // eslint-disable-next-line no-console
        console.error(`Stream with streamId ${streamId} not found`);

        return false;
    }

    // check if it is semi pro or pro
    if (stream.semiPro || stream.pro) {
        // and if user is logged in
        const isLogged = !!getState().profile.jwt;

        if (!isLogged) {
            if (userInitiated) {
                // show an alert. Note: The Alert does not show when called in DidMount. Not sure why. This is also a reason for the userInitiated flag.
                showInfo('É necessário estar logado para acessar esse streaming.');

                // show the login box
                dispatch(setProfileModalState(true));
            } else {
                // Note: this is the case when the streamId is set from the local storage and it was a (semi) pro stream but is now not more logged in (for any reason other than clicking the logout button)
                // reset the local storage
                localstorage.remove('streamId');

                // eslint-disable-next-line no-console
                console.log('reset stream because user is not (more) logged in');
            }

            return false;
        }

        if (stream.pro) {
            // this is a pro stream, check if user isPro
            const user = getState().profile.user;

            if (!user || !user.isPro) {
                if (userInitiated) {
                    // show an alert. Note: The Alert does not show when called in DidMount. Not sure why. This is also a reason for the userInitiated flag.
                    showInfo('É necessário ser um usuário PRO pra acessar esse stream.', { duration: Infinity, extraClass: 'pro', buttons: [{ text: 'Seja um assinante!', callback: () => history.push('/vantagens-da-assinatura-pro') }] });
                } else {
                    // Note: this is the case when the streamId is set from the local storage and it was a (semi) pro stream but is now not more logged in (for any reason other than clicking the logout button)
                    // reset the local storage
                    localstorage.remove('streamId');

                    // eslint-disable-next-line no-console
                    console.log('reset stream because user is not (more) PRO'); // not sure when or if this ever can happen...
                }

                return false;
            }
        }
    }

    dispatch(setCurrentStream(streamId));

    // update local storage
    localstorage.set('streamId', streamId);

    // refresh the meta data
    dispatch(fetchNowPlayingIfNeeded());


    if (userInitiated && stream.isPopup) {
        // open a popup with the player (currently only the alternativo player)
        const newWindow = window.open(stream.popupUrl, stream.popupId, stream.popupConfig);

        dispatch(unlockPlayer());

        dispatch(setIsLive(true));

        if (newWindow) {
            // Browser has allowed it to be opened
            newWindow.focus();

            newWindow.onload = () => {
                // we could do stuff here that needs the new window to be loaded.
                // For example initialize some callback functions to remotely control the other window...
            };
        } else {
            // Browser has blocked it
            showError('Para acessar esse streaming, é necessário habilitar os pop-ups.');

            // eslint-disable-next-line no-console
            console.error("Pop-ups disabled, can't open the popup player");
        }
    }

    return true;
};

/**
 * Set the streams
 *
 * @params {{type: array}}
 */
const setAvailableStreams = (availableStreams) => ({
    type: SET_AVAILABLE_STREAMS,
    payload: { availableStreams },
});

/**
 * Get the available streams
 *
 * @returns {{type: boolean}} (if successfull or not)
 *
 */
export const getAvailableStreams = () => (dispatch) => {
    const url = '/api/v1/aovivo/getAvailableStreams';

    return Api.get(url)
        .then((json) => {
            if (json.status === 'success') {
                return dispatch(setAvailableStreams(json.data));
            }

            throw `error from server: ${json.message}`;
        })
        .catch((err) => {
            // eslint-disable-next-line no-console
            console.log('got error when fetching streams:', err);
        });
};

/**
 * Get the available pro streams
 *
 * @returns {{type: boolean}} (if successfull or not)
 *
 */
export const getAvailableProStreams = () => (dispatch, getState) => {
    const url = '/api/v1/aovivo/getAvailableProStreams';
    const token = getState().profile.jwt;

    return Api.get(url, token)
        .then((json) => {
            if (json.status === 'success') {
                dispatch(setAvailableStreams(json.data));

                // check if there is a streamId in the local storage
                const streamId = localstorage.get('streamId');

                if (streamId) {
                    // restore the users stream

                    setTimeout(() => dispatch(setStream(streamId)), 200); // To be sure that the store has updated. Not the best solution but ok for now
                }

                return true;
            }

            throw `error from server: ${json.message}`;
        })
        .catch((err) => {
            // eslint-disable-next-line no-console
            console.log('got error when fetching pro streams:', err);
        });
};
